import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { FaSlidersH, FaTimes } from 'react-icons/fa';
import ChartCard, { ChartCardIconButton, ChartCardButton } from '../shared/ChartCard.js';
import { buildExportSvg, downloadSvg } from '../shared/svgExport.js';
import {
    CONSUMPTION_GRADIENT,
    colorsAlongGradient,
    DARK_TEXT,
    MUTED_TEXT,
    FONT_STACK,
    formatValue,
    formatPercent,
    foldIntoOthers,
    polarToCartesian,
    distributeNatural,
    distributeEqually,
} from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';

const TOP_MARGIN = 40;
const CHART_LEGEND_GAP = 35;
const LEGEND_ROW_HEIGHT = 26;
const LEGEND_ITEM_GAP = 28;
const BOTTOM_PADDING = 56;
const LABEL_COL_GAP = 10;

const DEFAULT_SETTINGS = {
    fontSize: 12, // taille de police des labels connecteurs (légende = +0.5pt)
    connectorLength: 22, // longueur du 1er segment (diagonal court) des connecteurs
    connectorColor: '#c7cad3', // couleur des traits connecteurs
    chartSize: 150, // rayon du donut
    innerRadiusPercent: 60, // taille du trou central, en % du rayon
    ringThickness: 2, // épaisseur des séparateurs blancs entre segments
    padAngle: 1, // espace (degrés) entre segments
    labelSpacing: 18, // espacement vertical minimal entre labels connecteurs
    equalSpacing: false, // répartition équitable au lieu d'un espacement au plus près
    showPercent: true,
    roundPercent: false, // arrondit les % à l'unité (sauf en dessous de 1%, gardé en décimale)
    showValue: true,
    rotation: 0, // rotation du donut (degrés) — pour éloigner un segment d'une zone gênante
    centerFontSize: 26, // taille de police du total central (px)
    showConnectors: true, // afficher les labels reliés par des traits
    showLegend: true, // afficher la légende sous le graphique
    cornerRadius: 2, // arrondi des coins des arcs
};

// Compose "nom · valeur m³ (pourcentage %)" en respectant les préférences d'affichage.
const buildLabelParts = (value, percentage, unit, showValue, showPercent, roundPercent) => {
    const valuePart = showValue ? ` · ${formatValue(value)} ${unit}` : '';
    const percentPart = showPercent ? ` (${formatPercent(percentage, roundPercent)}%)` : '';
    return `${valuePart}${percentPart}`;
};

// Mesure réelle (canvas) plutôt qu'une estimation par nombre de caractères : les marges
// et le retour à la ligne de la légende dépendent d'une largeur exacte, sans quoi un
// texte plus large que prévu déborde du cadre du SVG exporté.
let measureCanvasCtx = null;
const estimateTextWidth = (text, fontSize = 12, weight = 500) => {
    if (!measureCanvasCtx) measureCanvasCtx = document.createElement('canvas').getContext('2d');
    measureCanvasCtx.font = `${weight >= 600 ? 700 : 500} ${fontSize}px ${FONT_STACK}`;
    return measureCanvasCtx.measureText(text).width + 6;
};

// Layer custom : labels externes pour chaque tranche, reliés par un connecteur en coude,
// répartis verticalement pour ne jamais se chevaucher (même technique que
// UsageTaxonomySunburstReportCard). Contrairement au sunburst, il n'y a ici qu'un seul
// niveau de tranches (pas de filtre par profondeur) et pas de rotation à rattraper à la
// main : `startAngle`/`endAngle` sont passés directement à Nivo Pie (cf. plus bas), qui
// renvoie déjà des `arc.startAngle/endAngle` post-rotation dans `dataWithArc`.
const ConnectorsLayer = ({ dataWithArc, centerX, centerY, radius, settings, unit, total }) => {
    if (!settings.showConnectors) return null;
    const right = [];
    const left = [];

    dataWithArc.forEach((d) => {
        const { startAngle, endAngle, outerRadius } = d.arc;
        const midAngle = (startAngle + endAngle) / 2;
        const anchor = polarToCartesian(centerX, centerY, outerRadius, midAngle);
        const side = Math.sin(midAngle) >= 0 ? 1 : -1;
        (side === 1 ? right : left).push({
            id: d.id,
            anchor,
            side,
            y: anchor.y,
            label: d.data.label,
            dotColor: d.data.color,
            value: d.value,
            percentage: total > 0 ? (d.value / total) * 100 : 0,
        });
    });

    const distribute = settings.equalSpacing ? distributeEqually : distributeNatural;
    const items = [...distribute(right, settings.labelSpacing), ...distribute(left, settings.labelSpacing)];
    const labelColX = radius + settings.connectorLength + LABEL_COL_GAP + 40;

    return (
        <g>
            {items.map((item) => {
                const elbow = { x: item.anchor.x + item.side * settings.connectorLength, y: item.y };
                const labelX = centerX + item.side * labelColX;
                const textX = labelX + item.side * 12; // laisse la place à la pastille
                return (
                    <g key={item.id}>
                        <polyline
                            points={`${item.anchor.x},${item.anchor.y} ${elbow.x},${elbow.y} ${labelX - item.side * 4},${item.y}`}
                            fill="none"
                            stroke={settings.connectorColor}
                            strokeWidth={1}
                        />
                        <circle cx={labelX} cy={item.y} r={4} fill={item.dotColor} />
                        <text
                            x={textX}
                            y={item.y}
                            textAnchor={item.side === 1 ? 'start' : 'end'}
                            dominantBaseline="central"
                            style={{ fontSize: settings.fontSize, fontFamily: FONT_STACK }}
                        >
                            <tspan fontWeight={600} fill={DARK_TEXT}>
                                {item.label}
                            </tspan>
                            <tspan fill={MUTED_TEXT}>
                                {buildLabelParts(item.value, item.percentage, unit, settings.showValue, settings.showPercent, settings.roundPercent)}
                            </tspan>
                        </text>
                    </g>
                );
            })}
        </g>
    );
};

// Layer custom : légende sobre sous le graphique, une ligne par tranche (pas de
// regroupement catégorie/sous-catégorie ici, la donnée est déjà plate), en grille qui se
// répartit sur plusieurs lignes si nécessaire.
const LegendLayer = ({ items, top, width, centerX, settings }) => {
    if (!settings.showLegend) return null;
    return (
        <g>
            <line x1={centerX - width / 2} y1={top - 12} x2={centerX + width / 2} y2={top - 12} stroke="#e8eaf3" strokeWidth={1} />
            {items.map((item) => (
                <g key={item.id} transform={`translate(${item.x}, ${item.y})`}>
                    <circle cx={6} cy={0} r={6} fill={item.color} />
                    <text x={18} y={0} dominantBaseline="central" style={{ fontSize: settings.fontSize + 0.5, fontFamily: FONT_STACK }}>
                        <tspan fontWeight={700} fill={DARK_TEXT}>
                            {item.label}
                        </tspan>
                        <tspan fill={MUTED_TEXT}>{item.labelSuffix}</tspan>
                    </text>
                </g>
            ))}
        </g>
    );
};

// Layer custom : le total au centre du donut, en texte SVG (capturé par l'export SVG,
// contrairement à un overlay HTML positionné par-dessus le graphique).
const CENTER_TEXT = '#205673'; // total central (et libellé TOTAL), bleu pétrole de la palette
const EXPORT_PADDING_RIGHT = 12; // marge ajoutée à droite du SVG exporté (px)

const CenterLayer = ({ centerX, centerY, total, unit, settings }) => {
    const valueSize = settings.centerFontSize;
    const eyebrowSize = Math.max(10, settings.centerFontSize * 0.28);
    return (
        <g style={{ pointerEvents: 'none' }}>
            <text
                x={centerX}
                y={centerY - valueSize * 0.32}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: valueSize, fontWeight: 700, fontFamily: FONT_STACK, fill: CENTER_TEXT }}
            >
                {formatValue(total)} {unit}
            </text>
            <text
                x={centerX}
                y={centerY + valueSize * 0.42}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: eyebrowSize, fontWeight: 600, fontFamily: FONT_STACK, fill: CENTER_TEXT, letterSpacing: '0.03em' }}
            >
                TOTAL
            </text>
        </g>
    );
};

const SETTINGS_SECTIONS = [
    {
        id: 'donut',
        title: 'Donut',
        fields: [
            { key: 'chartSize', label: 'Taille du donut', type: 'range', min: 110, max: 220, step: 5, unit: 'px' },
            { key: 'innerRadiusPercent', label: 'Taille du trou central', type: 'range', min: 0, max: 85, step: 5, unit: '%' },
            { key: 'cornerRadius', label: 'Arrondi des coins', type: 'range', min: 0, max: 24, step: 1, unit: 'px' },
            { key: 'ringThickness', label: 'Épaisseur des séparateurs', type: 'range', min: 0, max: 4, step: 0.5, unit: 'px' },
            { key: 'padAngle', label: 'Espace entre segments', type: 'range', min: 0, max: 10, step: 0.5, unit: '°' },
            { key: 'rotation', label: 'Rotation du graphique', type: 'range', min: 0, max: 355, step: 5, unit: '°' },
        ],
    },
    {
        id: 'connectors',
        title: 'Connecteurs',
        fields: [
            { key: 'connectorLength', label: 'Longueur des connecteurs', type: 'range', min: 0, max: 50, step: 2, unit: 'px' },
            { key: 'connectorColor', label: 'Couleur des connecteurs', type: 'color' },
            { key: 'labelSpacing', label: 'Espacement des labels', type: 'range', min: 12, max: 32, step: 1, unit: 'px' },
            { key: 'equalSpacing', label: 'Répartition équitable des labels', type: 'checkbox' },
        ],
    },
    {
        id: 'typography',
        title: 'Typographie',
        fields: [
            { key: 'fontSize', label: 'Taille de police', type: 'range', min: 10, max: 24, step: 1, unit: 'px' },
            { key: 'centerFontSize', label: 'Taille du total central', type: 'range', min: 16, max: 40, step: 1, unit: 'px' },
        ],
    },
    {
        id: 'content',
        title: 'Contenu affiché',
        fields: [
            { key: 'showValue', label: 'Afficher les valeurs', type: 'checkbox' },
            { key: 'showPercent', label: 'Afficher les pourcentages', type: 'checkbox' },
            { key: 'roundPercent', label: 'Arrondir les pourcentages', type: 'checkbox' },
            { key: 'showConnectors', label: 'Afficher les labels', type: 'checkbox' },
            { key: 'showLegend', label: 'Afficher la légende', type: 'checkbox' },
        ],
    },
];

const SettingsField = ({ field, settings, onChange }) => (
    <div className="sunburst-settings-row" key={field.key}>
        <label htmlFor={`donut-export-setting-${field.key}`}>{field.label}</label>
        {field.type === 'range' ? (
            <div className="sunburst-settings-range">
                <input
                    id={`donut-export-setting-${field.key}`}
                    type="range"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={settings[field.key]}
                    onChange={(e) => onChange(field.key, Number(e.target.value))}
                />
                <span className="sunburst-settings-value">
                    {settings[field.key]}
                    {field.unit}
                </span>
            </div>
        ) : field.type === 'color' ? (
            <input
                id={`donut-export-setting-${field.key}`}
                type="color"
                className="sunburst-settings-color"
                value={settings[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
            />
        ) : (
            <label className="sunburst-settings-toggle">
                <input
                    id={`donut-export-setting-${field.key}`}
                    type="checkbox"
                    checked={settings[field.key]}
                    onChange={(e) => onChange(field.key, e.target.checked)}
                />
                <span className="sunburst-settings-toggle-slider" />
            </label>
        )}
    </div>
);

const SettingsPanel = ({ settings, onChange, onClose }) => (
    <div className="sunburst-settings-panel">
        <div className="sunburst-settings-panel-header">
            <span>Réglages d'affichage</span>
            <button type="button" className="sunburst-settings-close" onClick={onClose} aria-label="Fermer">
                <FaTimes />
            </button>
        </div>
        <div className="sunburst-settings-grid">
            {SETTINGS_SECTIONS.map((section) => (
                <div className="sunburst-settings-section" key={section.id}>
                    <div className="sunburst-settings-section-title">{section.title}</div>
                    {section.fields.map((field) => (
                        <SettingsField key={field.key} field={field} settings={settings} onChange={onChange} />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   Mode Export de "Répartition des consommations" — pensé pour être copié/exporté tel
   quel dans un rapport client. Même parti pris visuel que
   UsageTaxonomySunburstReportCard (connecteurs externes, légende, panneau de réglages,
   export SVG), mais pour une donnée à plat (Pie, pas Sunburst) : pas de notion de
   catégorie/sous-catégorie ici, donc pas besoin de la mécanique de simulation
   d'écart/rotation propre au Sunburst — Nivo Pie supporte nativement padAngle et
   startAngle/endAngle (rotation).

   @param {Record<string, number>} data - {label: valeur}, même format que UsageDonutChartNivo
   @param {string} unit
   @param {string} [title]
───────────────────────────────────────────── */
const UsageDonutExportView = ({ data, unit, title }) => {
    const containerRef = useRef(null);
    const [downloadState, setDownloadState] = useState('idle');
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [panelOpen, setPanelOpen] = useState(false);
    const updateSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

    // Fira Sans se charge de façon asynchrone (Google Fonts) : cf. UsageTaxonomySunburstReportCard.
    const [fontsReadyTick, setFontsReadyTick] = useState(0);
    useEffect(() => {
        if (typeof document !== 'undefined' && document.fonts?.ready) {
            document.fonts.ready.then(() => setFontsReadyTick((n) => n + 1));
        }
    }, []);

    const { labels, values } = useMemo(() => foldIntoOthers(Object.keys(data), Object.values(data)), [data]);
    const total = Math.round(values.reduce((sum, v) => sum + v, 0));

    const items = useMemo(() => {
        const colors = colorsAlongGradient(labels, CONSUMPTION_GRADIENT, (label) => label === 'Autres');
        return labels.map((label, i) => ({ id: label, label, value: values[i], color: colors[i] }));
    }, [labels, values]);

    // Marge latérale calculée à partir du plus long label réellement affiché en connecteur.
    const sideMargin = useMemo(() => {
        const maxLabelWidth = items.reduce((max, item) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0;
            const text = `${item.label}${buildLabelParts(item.value, pct, unit, settings.showValue, settings.showPercent, settings.roundPercent)}`;
            return Math.max(max, estimateTextWidth(text, settings.fontSize, 600));
        }, 0);
        return Math.max(140, settings.connectorLength + LABEL_COL_GAP + 40 + maxLabelWidth + 18);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, total, unit, settings.showValue, settings.showPercent, settings.roundPercent, settings.fontSize, settings.connectorLength, fontsReadyTick]);

    const chartWidth = settings.chartSize * 2 + sideMargin * 2;

    // Légende : grille à colonnes de largeur fixe (= le plus large libellé mesuré).
    const legend = useMemo(() => {
        const legendItems = items.map((item) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0;
            const labelSuffix = buildLabelParts(item.value, pct, unit, settings.showValue, settings.showPercent, settings.roundPercent);
            return {
                id: item.id,
                label: item.label,
                labelSuffix,
                value: item.value,
                percentage: pct,
                color: item.color,
                width: 18 + estimateTextWidth(`${item.label}${labelSuffix}`, settings.fontSize + 0.5, 700),
            };
        });
        const maxItemWidth = legendItems.reduce((max, it) => Math.max(max, it.width), 0);
        const columnWidth = maxItemWidth + LEGEND_ITEM_GAP;
        const availableWidth = chartWidth - 48;
        let columns = Math.min(legendItems.length, 4);
        while (columns > 1 && columns * columnWidth > availableWidth) columns -= 1;

        const rowCount = columns > 0 ? Math.ceil(legendItems.length / columns) : 0;
        const positioned = legendItems.map((item, i) => ({
            ...item,
            col: columns > 0 ? i % columns : 0,
            row: columns > 0 ? Math.floor(i / columns) : 0,
        }));
        const rowCounts = Array.from({ length: rowCount }, (_, r) => positioned.filter((it) => it.row === r).length);
        const widestRow = rowCounts.length ? Math.max(...rowCounts) * columnWidth : 0;
        return { items: positioned, rowCount, columnWidth, rowCounts, widestRow };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, chartWidth, total, unit, settings.showValue, settings.showPercent, settings.roundPercent, settings.fontSize, fontsReadyTick]);

    const contentBottom = settings.chartSize * 2 + settings.labelSpacing * 3 + settings.fontSize;
    const legendTop = TOP_MARGIN + contentBottom + (settings.showLegend ? CHART_LEGEND_GAP : 0);
    const legendHeight = settings.showLegend ? legend.rowCount * LEGEND_ROW_HEIGHT : 0;
    const chartHeight = legendTop + legendHeight + BOTTOM_PADDING;

    const localCenterX = settings.chartSize;
    const localLegendTop = contentBottom + (settings.showLegend ? CHART_LEGEND_GAP : 0);

    const legendPositioned = useMemo(
        () =>
            legend.items.map((item) => {
                const rowWidth = legend.rowCounts[item.row] * legend.columnWidth;
                const offsetX = localCenterX - rowWidth / 2;
                return { ...item, x: offsetX + item.col * legend.columnWidth, y: localLegendTop + item.row * LEGEND_ROW_HEIGHT };
            }),
        [legend, localCenterX, localLegendTop]
    );

    const handleDownloadSvg = () => {
        const svgEl = containerRef.current?.querySelector('svg');
        if (!svgEl) return;
        downloadSvg(buildExportSvg(svgEl, { paddingRight: EXPORT_PADDING_RIGHT }), 'repartition-des-consommations-rapport.svg');
        setDownloadState('downloaded');
        setTimeout(() => setDownloadState('idle'), 2000);
    };

    const renderConnectorsLayer = useCallback(
        (layerProps) => <ConnectorsLayer {...layerProps} settings={settings} unit={unit} total={total} />,
        [settings, unit, total]
    );

    const renderLegendLayer = useCallback(
        () => (
            <LegendLayer
                items={legendPositioned}
                top={localLegendTop}
                width={legend.widestRow}
                centerX={localCenterX}
                settings={settings}
            />
        ),
        [legendPositioned, localLegendTop, legend.widestRow, localCenterX, settings]
    );

    const renderCenterLayer = useCallback(
        ({ centerX, centerY }) => <CenterLayer centerX={centerX} centerY={centerY} total={total} unit={unit} settings={settings} />,
        [total, unit, settings]
    );

    return (
        <ChartCard
            title={title || 'Répartition des consommations'}
            bodyClassName="sunburst-report-body"
            actions={
                <>
                    <ChartCardIconButton
                        onClick={() => setPanelOpen((v) => !v)}
                        title="Réglages d'affichage"
                        aria-label="Réglages d'affichage"
                    >
                        <FaSlidersH />
                    </ChartCardIconButton>
                    <ChartCardButton onClick={handleDownloadSvg} title="Télécharger le graphique en SVG">
                        {downloadState === 'downloaded' ? 'SVG téléchargé' : 'Télécharger en SVG'}
                    </ChartCardButton>
                </>
            }
            panel={panelOpen && <SettingsPanel settings={settings} onChange={updateSetting} onClose={() => setPanelOpen(false)} />}
        >
            <div className="sunburst-chart-area" ref={containerRef} style={{ width: chartWidth, height: chartHeight }}>
                <ResponsivePie
                    data={items}
                    id="id"
                    value="value"
                    valueFormat={(v) => `${formatValue(v)} ${unit}`}
                    margin={{
                        top: TOP_MARGIN,
                        right: sideMargin,
                        bottom: chartHeight - TOP_MARGIN - settings.chartSize * 2,
                        left: sideMargin,
                    }}
                    innerRadius={settings.innerRadiusPercent / 100}
                    padAngle={settings.padAngle}
                    cornerRadius={settings.cornerRadius}
                    startAngle={settings.rotation}
                    endAngle={settings.rotation + 360}
                    borderWidth={settings.ringThickness}
                    borderColor="#ffffff"
                    colors={(d) => d.data.color}
                    enableArcLabels={false}
                    enableArcLinkLabels={false}
                    animate={true}
                    motionConfig="gentle"
                    theme={{ text: { fontFamily: FONT_STACK } }}
                    layers={['arcs', renderConnectorsLayer, renderLegendLayer, renderCenterLayer]}
                    tooltip={({ datum }) => (
                        <div className="sunburst-tooltip">
                            <div className="sunburst-tooltip-head">
                                <span className="sunburst-tooltip-swatch" style={{ background: datum.data.color }} />
                                <div className="sunburst-tooltip-label">{datum.label}</div>
                            </div>
                            <div className="sunburst-tooltip-value">
                                {formatValue(datum.value)} {unit}
                            </div>
                            <div className="sunburst-tooltip-percent">
                                {formatPercent(total > 0 ? (datum.value / total) * 100 : 0, settings.roundPercent)}% du total
                            </div>
                        </div>
                    )}
                />
            </div>
        </ChartCard>
    );
};

export default UsageDonutExportView;
